import { Box, Button, CircularProgress, Container, Paper, Table, TableBody, TableCell, TableRow, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import IdleTimerContainer from '../../components/IdleTimerContainer';
import { useEffect, useRef, useState } from 'react';
import axios, { AxiosError } from 'axios';
import { apiURL } from '../../constants/apiUrlConstants';
import Loader from '../../components/Loader';
import KuwaitiDinarFormat from '../../components/KuwaitiDinarFormat';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getRequest, postRequest } from '../../services/httpservice';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckIcon from "../../assets/Check.svg"; 
interface SessionPaymentStatusResponse {
  transpaymentId: string | number;
  dtTransactionDate: string;
  charges: number | string;
  paymentMethod: string;
  amount: number | string;
  civilId: string;
  authCode: string;
  knetbank_reference?: string;
  knetpaymentid?: string;
  knettransactionid?: string;
  paymentStatus: string;
  status: string;
  message: string;
}

function PaymentStatus() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { refId } = useParams<{ refId?: string }>();
  const [civilId, setLocalCivilId] = useState<string | null>(null);
  const [paymentStatusData, setPaymentStatusData] = useState<SessionPaymentStatusResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [toastCloseInMs, setToastCloseInMs] = useState<number>(0);
  const [name, setName] = useState<string>('');
  const [nameArabic, setNameArabic] = useState<string>('');
  const retryCountRef = useRef<number>(0);

  useEffect(() => {
    if (window.location.search) {
      const urlWithoutParams = window.location.origin + window.location.pathname;
      window.history.replaceState({}, document.title, urlWithoutParams);
    }

    const storedName = sessionStorage.getItem('name');
    const storedNameArabic = sessionStorage.getItem('nameArabic');
    if (storedName && storedName !== 'null') {
      setName(storedName);
    }
    if (storedNameArabic && storedNameArabic !== 'null' && storedNameArabic !== '') {
      if (storedName && storedName !== 'null' && storedName !== '') {
        setName(storedName + ' / ');
      }
      setNameArabic(storedNameArabic);
    }

    const msToast = process.env.REACT_APP_TOAST_CLOSE_IN_SECONDS;
    if (msToast) {
      setToastCloseInMs(parseInt(msToast));
    }

    getPaymentStatus(refId || null);
  }, [refId]);

  const getPaymentStatus = async (refIdParam: string | null) => {
    try {
      const storedCivilId = sessionStorage.getItem('civilId');
      if (storedCivilId) {
        setLocalCivilId(storedCivilId);
      }
      const accessToken = sessionStorage.getItem('accessToken');
      if (!accessToken) {
        navigate('/');
        return;
      }

      if (!refIdParam) {
        toast.error('Invalid payment reference');
        setIsLoading(false);
        return;
      }

      const response = await postRequest(
        `${process.env.REACT_APP_API_URL}${apiURL.GET_TRANS_BY_REF_ID}`,
        { civilId: storedCivilId, refId: refIdParam }
      );

      if (response === null) {
        setIsLoading(false);
        toast.dismiss();
        toast.error(process.env.REACT_APP_ERROR_MSG);
        return;
      }
      if (response.status === 401) {
        setIsLoading(false);
        toast.error('Session expired');
        setTimeout(() => { navigate('/'); }, 2000);
        return;
      }
      if (response.data === '') {
        setIsLoading(false);
        toast.dismiss();
        toast.error(process.env.REACT_APP_ERROR_MSG);
        return;
      }

      if (response.data?.paymentStatus?.toLowerCase() === 'pending' && retryCountRef.current < 4) {
        retryCountRef.current += 1;
        // toast.info('Payment is processing');
        setTimeout(() => getPaymentStatus(refIdParam), 5000);
        return;
      }

      setIsLoading(false);
      setPaymentStatusData(response.data);
    } catch (error) {
      setIsLoading(false);
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError;
        if (axiosError.response?.status === 401) {
          toast.error('Session expired');
          setTimeout(() => { navigate('/'); }, 2000);
        } else {
          toast.error(process.env.REACT_APP_ERROR_MSG);
        }
      }
    }
  };

  const isSuccess = paymentStatusData?.paymentStatus === 'APPROVED';
  const isKnet = paymentStatusData?.paymentMethod?.toLowerCase() === 'knet';


const isPending =  paymentStatusData?.paymentStatus === 'PENDING';
  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleString('en-GB', {
        day: 'numeric', month: 'numeric', year: 'numeric',
        hour: 'numeric', minute: 'numeric', hour12: false,
      }).replace(',', '');
    } catch {
      return dateStr;
    }
  };

  return (
    <>
      <Container className='mainContainer' maxWidth="xl" sx={{ mt: { xs: 24, sm: 4 }, mb: 10, minWidth: "375px" }}>
        <Box>
          {/* Blue header bar */}
          <Box className="headerSubheaderStyle headerColor headerBgColor" sx={{ px: 5 }}>
            <Box className="dealTitle pad-right-10"> {t('paymentStatus.paymentStatus')} <br /> </Box>
            {/* <Box className="trns-HeaderTitle" sx={{ fontSize: { xs: '0.9rem', sm: '1.2rem' } }}> {name}<span dir="rtl" lang="ar" className='pad-right-10'>{nameArabic}</span> </Box> */}
            <Box className="civilId">
              {t('deals.civilId')}<span dir="rtl" lang="ar" > - </span>{civilId}
            </Box>
          </Box>

          {/* PROCESSING STATE */}
          {isLoading && (
            <>
              <Box
                sx={{
                  p: 3, border: '1px solid #D0DAE0', background: '#fff',
                  borderRadius: '10px 10px 0 0'  ,  textAlign: 'left',
                }}
              >
                <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#2A92BD', mb: 0.5  ,  textAlign: 'left'}}>
                  {t('paymentStatus.paymentProcessing')}
                </Typography>
                <Typography sx={{ fontSize: '14px', color: '#E53935' ,  textAlign: 'left' }}>
                  {t('paymentStatus.doNotClose')}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: 'flex', justifyContent: 'center', background: '#fff',
                  border: '1px solid #D0DAE0', borderTop: 'none',
                  borderRadius: '0 0 10px 10px', py: 6, px: 2,
                }}
              >
                <Box sx={{ width: '100%', maxWidth: '500px' }}>
                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: '14px', overflow: 'hidden',
                      border: '1px solid #E2E8F0', boxShadow: '0px 4px 20px rgba(0,0,0,0.06)',
                    }}
                  >
                    <Box sx={{ background: '#2A92BD', py: 4, px: 3, textAlign: 'center' }}>
                      <CircularProgress
                        size={70}
                        thickness={3}
                        sx={{ color: '#43E355', mb: 2 }}
                      />
                      <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#fff' }}>
                        {t('paymentStatus.paymentProcessing')}
                      </Typography>
                    </Box>
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                      <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#00104B', mb: 1 }}>
                        {t('paymentStatus.sending')}
                      </Typography>
                      <Typography sx={{ fontSize: '20px', color: '#7A7A7A', lineHeight: 1.7 }}>
                        {t('paymentStatus.processingDesc')}
                      </Typography>
                    </Box>
                  </Paper>
                </Box>
              </Box>
            </>
          )}

          {/* SUCCESS / FAILURE STATE */}
          {!isLoading && paymentStatusData && (
            <>
              <Box
                sx={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  p: 3, border: '1px solid #D0DAE0', background: '#fff',
                  borderRadius: '10px 10px 0 0',
                }}
              >
                {/* <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#2A92BD' }}>
                  {isSuccess ? t('paymentStatus.paymentSuccessful') : t('paymentStatus.paymentFailed')}
                </Typography> */}
                <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#2A92BD' }}>
                 {isSuccess
                  ? t('paymentStatus.paymentSuccessful')
                  : isPending
                  ?    t('paymentStatus.paymentInProgress')
                  : t('paymentStatus.paymentFailed')}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: 'flex', justifyContent: 'center', background: '#fff',
                  border: '1px solid #D0DAE0', borderTop: 'none',
                  borderRadius: '0 0 10px 10px', py: 6, px: 2,
                }}
              >
                <Box sx={{ width: '100%', maxWidth: '500px' }}>
                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: '14px', overflow: 'hidden',
                      border: '1px solid #E2E8F0', boxShadow: '0px 4px 20px rgba(0,0,0,0.06)',
                    }}
                  >
                    <Box sx={{ background: '#2A92BD', py: 4, px: 3, textAlign: 'center' }}>
                      {/* <Box
                        sx={{
                          width: '70px', height: '70px', borderRadius: '50%',
                          border: `3px solid ${isSuccess ? '#43E355' : '#FF4444'}`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          margin: '0 auto', mb: 2, fontSize: '32px', fontWeight: 700,
                          color: isSuccess ? '#43E355' : '#FF4444', background: 'transparent',
                        }}
                      >
                        {isSuccess ? '✓' : '✕'}
                      </Box> */}
                      <Box
                sx={{
                     width: 70,
                     height: 70,
                     borderRadius: '50%',
                     border: `3px solid ${
                     isSuccess ? '#43E355' : isPending ? '#FFC107' : '#FF4444'
                 }`,
                       display: 'flex',
                       alignItems: 'center',
                       justifyContent: 'center',
                       margin: '0 auto',
                       mb: 2,
                       fontSize: '32px',
                       fontWeight: 700,
                       color: isSuccess ? '#43E355' : isPending ? '#FFC107' : '#FF4444',
                 }}
                       >
              {
  isSuccess ? (
    <img
      src={CheckIcon}
      alt="Success"
      style={{ width: 80, height: 80 }}
    />
  ) : isPending ? (
    <AccessTimeIcon sx={{ fontSize: 80 }} />
  ) : (
    "✕"
  )
}
                   </Box>
                      {/* <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#fff' }}>
                        {isSuccess ? t('paymentStatus.paymentSuccessful') : t('paymentStatus.paymentFailed')}
                      </Typography> */}
                      <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#fff' }}>
                      {isSuccess
                      ? t('paymentStatus.paymentSuccessful')
                      : isPending
                      ? t('paymentStatus.paymentInProgress')
                      : t('paymentStatus.paymentFailed')}
                    </Typography>
                    </Box>

                    <Box sx={{ p: { xs: 2, sm: 4 } }}>
                      <Table sx={{ width: '100%', tableLayout: 'fixed' }}>
                        <TableBody>
                          <TableRow>
                            <TableCell sx={{ borderBottom: '1px solid #EDF2F7', color: '#00104B', fontWeight: 400, fontSize: '14px', py: 2, width: '50%', overflowWrap: 'break-word' }}>
                              {t('paymentStatus.transactionId')}
                            </TableCell>
                            <TableCell sx={{ borderBottom: '1px solid #EDF2F7', color: '#00104B', fontWeight: 500, fontSize: '14px', py: 2, width: '50%', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                              {paymentStatusData.transpaymentId}
                            </TableCell>
                          </TableRow>

                          <TableRow>
                            <TableCell sx={{ borderBottom: '1px solid #EDF2F7', color: '#00104B',  fontWeight: 400,fontSize: '14px', py: 2, overflowWrap: 'break-word' }}>
                              {t('paymentStatus.transactionDateTime')}
                            </TableCell>
                            <TableCell sx={{ borderBottom: '1px solid #EDF2F7', color: '#00104B', fontWeight: 500, fontSize: '14px', py: 2, overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                              {paymentStatusData.dtTransactionDate}
                            </TableCell>
                          </TableRow>

                          {/* <TableRow>
                            <TableCell sx={{ borderBottom: '1px solid #EDF2F7', color: '#00104B',  fontWeight: 400,fontSize: '14px', py: 2 }}>
                              {t('paymentStatus.charges')}
                            </TableCell>
                            <TableCell sx={{ borderBottom: '1px solid #EDF2F7', color: '#00104B', fontWeight: 500, fontSize: '14px', py: 2 }}>
                              <KuwaitiDinarFormat amount={Number(paymentStatusData.charges)} />
                            </TableCell>
                          </TableRow> */}

                          <TableRow>
                            <TableCell sx={{ borderBottom: '1px solid #EDF2F7', color: '#00104B',  fontWeight: 400,fontSize: '14px', py: 2, overflowWrap: 'break-word' }}>
                              {t('paymentStatus.paymentMethod')}
                            </TableCell>
                            <TableCell sx={{ borderBottom: '1px solid #EDF2F7', color: '#00104B', fontWeight: 500, fontSize: '14px', py: 2, overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                              {paymentStatusData.paymentMethod}
                            </TableCell>
                          </TableRow>

                          <TableRow>
                            <TableCell
                              sx={{
                                borderBottom: (!isSuccess || isKnet) ? '1px solid #EDF2F7' : 'none',
                                color: '#00104B',  fontWeight: 400,fontSize: '14px', py: 2, overflowWrap: 'break-word',
                              }}
                            >
                              {isSuccess ? t('paymentStatus.totalAmountPaid') : t('paymentStatus.totalAmount')}
                            </TableCell>
                            <TableCell
                              sx={{
                                borderBottom: (!isSuccess || isKnet) ? '1px solid #EDF2F7' : 'none',
                                color: '#00104B', fontWeight: 500, fontSize: '14px', py: 2, overflowWrap: 'break-word', wordBreak: 'break-word',
                              }}
                            >
                              <KuwaitiDinarFormat amount={Number(paymentStatusData.amount)} />
                            </TableCell>
                          </TableRow>

                          {isKnet && (
                            <>
                              <TableRow>
                                <TableCell sx={{ borderBottom: '1px solid #EDF2F7', color: '#00104B', fontWeight: 400, fontSize: '14px', py: 2, overflowWrap: 'break-word' }}>
                                  {t('paymentStatus.knetBankReference')}
                                </TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #EDF2F7', color: '#00104B', fontWeight: 500, fontSize: '14px', py: 2, overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                                  {paymentStatusData.knetbank_reference}
                                </TableCell>
                              </TableRow>

                              <TableRow>
                                <TableCell sx={{ borderBottom: !isSuccess ? '1px solid #EDF2F7' : 'none', color: '#00104B', fontWeight: 400, fontSize: '14px', py: 2, overflowWrap: 'break-word' }}>
                                  {t('paymentStatus.knetPaymentId')}
                                </TableCell>
                                <TableCell sx={{ borderBottom: !isSuccess ? '1px solid #EDF2F7' : 'none', color: '#00104B', fontWeight: 500, fontSize: '14px', py: 2, overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                                  {paymentStatusData.knetpaymentid}
                                </TableCell>
                              </TableRow>
                            </>
                          )}

                          {!isSuccess && (
                            <TableRow>
                              <TableCell sx={{ borderBottom: 'none', color: '#00104B', fontSize: '14px', py: 2, overflowWrap: 'break-word' }}>
                                {t('paymentStatus.reason')}
                              </TableCell>
                              <TableCell sx={{ borderBottom: 'none', color: '#00104B', fontWeight: 400, fontSize: '14px', py: 2, overflowWrap: 'break-word', wordBreak: 'break-word' }}>
                                {paymentStatusData.message}
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </Box>
                  </Paper>

                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 5, width: '100%' }}>
                    <Button
                      variant="contained"
                      sx={{
                        background: '#2A92BD', borderRadius: '8px', width: '100%', maxWidth: '100%',
                        fontWeight: 500, fontSize: '14px', padding: '6px 16px', textTransform: 'none',
                        boxShadow: 'none', '&:hover': { background: '#2A92BD', boxShadow: 'none' },
                      }}
                      onClick={() => navigate('/home/transactions')}
                    >
                      {t('paymentStatus.goToTransactions')}
                    </Button>
                    {/* <Box
                      sx={{
                        mt: 2, width: '450px', maxWidth: '100%',
                        textAlign: 'center', fontSize: '14px', color: '#7A7A7A', lineHeight: 1.6,
                      }}
                    >
                      {t('paymentStatus.paymentConfirmation')}
                    </Box> */}
                  </Box>
                </Box>
              </Box>
            </>
          )}
        </Box>

        <ToastContainer position="top-right" newestOnTop limit={1} autoClose={toastCloseInMs} />
        <IdleTimerContainer />
      </Container>
    </>
  );
}

export default PaymentStatus;
